/*
 * vv-line-split.js — one cart line per design (order #129091, 9 Oct 2026).
 *
 * Bug: a customer ordering six table covers with six different designs pressed "Order with my own
 * design" six times. Each add carried the same variant and the same production properties, and no
 * artwork yet, so Shopify treated them as the same item and raised one line to quantity 6. The
 * portal then offered one upload box for six different designs.
 *
 * Fix: every add-to-cart of a configured print product (it carries line properties) that does NOT
 * already carry a design identity gets a hidden, unique `_Line_Ref` property, so Shopify keeps it as
 * its own line. Same mechanism the cart's "Duplicate" button already uses (`_dup`).
 *
 * Deliberately untouched:
 *   - items with no properties (fees, add-ons, Design Check, Secured Delivery): they must keep merging,
 *     because the cart reconciles them by variant id;
 *   - items that already identify their design (_CC_Design_ID, _CC_Project_ID, _AI_Finalised_Design,
 *     _AI_Fee_Identity, _AI_Design_Fee_For, _Design_Brief_Ref, _Artwork_Files, _BigHead_Preview, _dup):
 *     different designs are already separate lines, and the same design re-added should still merge;
 *   - /cart/change, /cart/update: never altered.
 * Pricing is not changed by this file. Quantity tiers (ITORIS WQB) are per line, as before.
 *
 * Covers every route a theme or app script uses to add: fetch, XMLHttpRequest (jQuery / ITORIS),
 * native form submit and form.submit(). transform() is pure and node-testable (test/line-split.test.mjs).
 */
(function (root) {
  "use strict";
  var KEY = "_Line_Ref";
  var DESIGN_KEYS = /^_?(CC_Design_ID|CC_Project_ID|AI_Finalised_Design|AI_Fee_Identity|AI_Design_Fee_For|Design_Brief_Ref|Artwork_Files|BigHead_Preview|dup)$/;
  // An existing _Line_Ref is never a reason to skip: a FormData copied from a form that was submitted
  // before would carry the OLD ref and silently merge again, so the ref is always replaced with a new one.

  function newRef() {
    var r = "";
    try { var a = new Uint32Array(2); root.crypto.getRandomValues(a); r = a[0].toString(36) + a[1].toString(36); }
    catch (e) { r = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2); }
    return Date.now().toString(36) + "-" + r.slice(0, 10);
  }
  function isCartAdd(url) {
    var u = String(url || "").toLowerCase().split("?")[0].split("#")[0];
    return /\/cart\/add(\.js|\.json)?\/?$/.test(u);
  }
  /** keys = the item's property names (without the properties[...] wrapper) */
  function wants(keys) {
    keys = keys.filter(function (k) { return k !== KEY; });
    if (!keys.length) return false;                 // fee / add-on / bare variant: leave merging alone
    for (var i = 0; i < keys.length; i++) if (DESIGN_KEYS.test(keys[i])) return false;
    return true;
  }
  function propKeysOfPairs(pairs) {                 // [[name, value]] from form/urlencoded bodies
    var out = [];
    pairs.forEach(function (p) { var m = /^properties\[(.+)\]$/.exec(p[0]); if (m) out.push(m[1]); });
    return out;
  }
  function hasItemsArray(pairs) {                   // items[0][id]=… urlencoded plural form
    for (var i = 0; i < pairs.length; i++) if (/^items\[\d+\]/.test(pairs[i][0])) return true;
    return false;
  }
  function itemsPairs(pairs) {                      // group items[n][properties][k] by n
    var groups = {};
    pairs.forEach(function (p) {
      var m = /^items\[(\d+)\]\[properties\]\[(.+)\]$/.exec(p[0]);
      if (m) (groups[m[1]] = groups[m[1]] || []).push(m[2]);
      var n = /^items\[(\d+)\]\[id\]$/.exec(p[0]); if (n) groups[n[1]] = groups[n[1]] || [];
    });
    return groups;
  }

  /* Pairs-based containers (FormData, URLSearchParams). Mutates in place; returns number of refs set. */
  function fixPairs(c) {
    var pairs = []; c.forEach(function (v, k) { pairs.push([k, v]); });
    if (hasItemsArray(pairs)) {
      var g = itemsPairs(pairs), n = 0;
      Object.keys(g).forEach(function (i) { if (wants(g[i])) { c.set("items[" + i + "][properties][" + KEY + "]", newRef()); n++; } });
      return n;
    }
    if (!wants(propKeysOfPairs(pairs))) return 0;
    c.set("properties[" + KEY + "]", newRef());
    return 1;
  }
  function fixJsonObj(o) {
    if (!o || typeof o !== "object") return 0;
    var n = 0;
    if (Array.isArray(o.items)) {
      o.items.forEach(function (it) {
        if (it && it.properties && typeof it.properties === "object" && wants(Object.keys(it.properties))) { it.properties[KEY] = newRef(); n++; }
      });
      return n;
    }
    if (o.properties && typeof o.properties === "object" && wants(Object.keys(o.properties))) { o.properties[KEY] = newRef(); n++; }
    return n;
  }
  /**
   * transform(body) → { body, n }  (n = refs added). Never throws; anything it can't read is returned as-is.
   */
  function transform(body) {
    try {
      if (body == null) return { body: body, n: 0 };
      if (typeof FormData !== "undefined" && body instanceof FormData) return { body: body, n: fixPairs(body) };
      if (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams) return { body: body, n: fixPairs(body) };
      if (typeof body === "string") {
        var s = body.replace(/^\s+/, "");
        if (s.charAt(0) === "{") { var o = JSON.parse(s), n = fixJsonObj(o); return n ? { body: JSON.stringify(o), n: n } : { body: body, n: 0 }; }
        if (s.indexOf("=") > -1) { var u = new URLSearchParams(body), m = fixPairs(u); return m ? { body: u.toString(), n: m } : { body: body, n: 0 }; }
      }
    } catch (e) {}
    return { body: body, n: 0 };
  }
  function note(n, via) { try { if (n) (root.__vvLineSplit = root.__vvLineSplit || []).push({ t: Date.now(), n: n, via: via }); } catch (e) {} }

  function install(w) {
    if (!w || w.__vvLineSplitOn) return; w.__vvLineSplitOn = 1;
    // fetch
    if (typeof w.fetch === "function") {
      var of = w.fetch;
      w.fetch = function (input, init) {
        try {
          var url = typeof input === "string" ? input : (input && input.url);
          var method = String((init && init.method) || (input && input.method) || "GET").toUpperCase();
          if (method === "POST" && isCartAdd(url) && init && init.body != null) {
            var r = transform(init.body);
            if (r.n) { init = Object.assign({}, init, { body: r.body }); note(r.n, "fetch"); }
          }
        } catch (e) {}
        return of.call(this, input, init);
      };
    }
    // XMLHttpRequest (jQuery, ITORIS)
    var X = w.XMLHttpRequest && w.XMLHttpRequest.prototype;
    if (X && X.open && X.send) {
      var oo = X.open, os = X.send;
      X.open = function (method, url) { try { this.__vvAdd = String(method || "").toUpperCase() === "POST" && isCartAdd(url); } catch (e) {} return oo.apply(this, arguments); };
      X.send = function (body) {
        try { if (this.__vvAdd) { var r = transform(body); if (r.n) { note(r.n, "xhr"); return os.call(this, r.body); } } } catch (e) {}
        return os.apply(this, arguments);
      };
    }
    // native form posts (submit event + programmatic form.submit(), which fires no event)
    function prepForm(f) {
      try {
        if (!f || !isCartAdd(f.getAttribute("action") || f.action) || String(f.method || "").toLowerCase() !== "post") return;
        var old = f.querySelector('input[data-vv-line-ref]'); if (old) old.parentNode.removeChild(old);
        var pairs = []; new FormData(f).forEach(function (v, k) { pairs.push([k, v]); });
        if (!wants(propKeysOfPairs(pairs))) return;
        var stale = f.querySelectorAll('input[name="properties[' + KEY + ']"]');
        for (var i = 0; i < stale.length; i++) stale[i].disabled = true;
        var inp = w.document.createElement("input");
        inp.type = "hidden"; inp.name = "properties[" + KEY + "]"; inp.value = newRef(); inp.setAttribute("data-vv-line-ref", "1");
        f.appendChild(inp); note(1, "form");
        // take it out again once the form data set has been built, so a later FormData(form) copy
        // never inherits this ref (it would merge into this line)
        w.setTimeout(function () { try { if (inp.parentNode) inp.parentNode.removeChild(inp); for (var j = 0; j < stale.length; j++) stale[j].disabled = false; } catch (e) {} }, 0);
      } catch (e) {}
    }
    try { w.document.addEventListener("submit", function (e) { if (!e.defaultPrevented) prepForm(e.target); }, true); } catch (e) {}
    var F = w.HTMLFormElement && w.HTMLFormElement.prototype;
    if (F && F.submit) { var ofs = F.submit; F.submit = function () { prepForm(this); return ofs.apply(this, arguments); }; }
  }

  var api = { transform: transform, wants: wants, isCartAdd: isCartAdd, install: install, KEY: KEY };
  if (typeof module !== "undefined" && module.exports) module.exports = api;
  else { root.VVLineSplit = api; install(root); }
})(typeof window !== "undefined" ? window : globalThis);
